import {
  Checkbox,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Select,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FaChevronDown } from "react-icons/fa";
import { FiEdit2 } from "react-icons/fi";
import { useState } from "react";
import {
  getRangeDisplayLabel,
  getRangeOptions,
  isCustomRangeValue,
} from "../../Shared/Utils/dateRanges";
import { toDisplayText } from "../../../../utils/localizedText";
import { ReportActionButton } from "../../Shared/Components/ReportActionButton";
import { ReportMenuButton } from "../../Shared/Components/ReportMenuButton";
import { reportToolbarStyles } from "../../Shared/Styles/reportToolbarStyles";

const MultiSelectMenu = ({
  label,
  onSelectionChange,
  options,
  selectedValues,
}) => (
  <FormControl>
    <FormLabel fontSize="sm">{label}</FormLabel>
    <Menu closeOnSelect={false}>
      <ReportMenuButton w="full">
        <HStack justify="space-between" w="full">
          <Text noOfLines={1}>
            {selectedValues.length === 0
              ? "Select"
              : selectedValues
                  .map((value) =>
                    toDisplayText(
                      options.find((option) => option.id === value)?.name,
                      value,
                    ),
                  )
                  .filter(Boolean)
                  .join(", ")}
          </Text>
          <Icon as={FaChevronDown} />
        </HStack>
      </ReportMenuButton>
      <MenuList
        {...reportToolbarStyles.menuList}
        maxHeight="200px"
        overflowY="auto"
      >
        {options.map((option) => (
          <MenuItem
            key={option.id}
            onClick={(event) => {
              event.stopPropagation();
              onSelectionChange(
                selectedValues.includes(option.id)
                  ? selectedValues.filter((value) => value !== option.id)
                  : [...selectedValues, option.id],
              );
            }}
          >
            <Checkbox isChecked={selectedValues.includes(option.id)} mr={3} />
            {toDisplayText(option.name, option.id)}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  </FormControl>
);

export const TrialBalanceFilters = ({
  branches,
  fetchBranches,
  filters,
  isLoadingBranches,
  onApplySettings,
  onOpenCustomRange,
  onSelectRange,
}) => {
  const [settings, setSettings] = useState({
    selectedBranches: filters.branches || [],
    with_pnl_openings: filters.with_pnl_openings ? "true" : "false",
    include_zero_balances: filters.include_zero_balances ? "true" : "false",
  });
  const rangeOptions = getRangeOptions({ reports: {} });
  const activeRangeLabel = filters.rangeKey
    ? getRangeDisplayLabel(filters.rangeKey, { reports: {} })
    : "Select Range";

  return (
    <HStack
      spacing={1}
      alignItems="center"
      justifyContent="center"
      flexWrap="wrap"
    >
      <ReportActionButton onClick={onOpenCustomRange}>
        {filters.rangeLabel}
      </ReportActionButton>
      <Menu>
        <ReportMenuButton>
          <HStack justify="space-between" w="full" minW="140px">
            <Text>{activeRangeLabel}</Text>
            <Icon as={FaChevronDown} />
          </HStack>
        </ReportMenuButton>
        <MenuList {...reportToolbarStyles.menuList}>
          {rangeOptions.map((range) => (
            <MenuItem
              key={range.value}
              onClick={() =>
                isCustomRangeValue(range.value)
                  ? onOpenCustomRange()
                  : onSelectRange(range.value)
              }
            >
              {range.label}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
      <Popover placement="bottom-end" isLazy onOpen={fetchBranches}>
        <PopoverTrigger>
          <ReportActionButton leftIcon={<FiEdit2 />}>
            More settings
          </ReportActionButton>
        </PopoverTrigger>
        <PopoverContent {...reportToolbarStyles.popoverContent} w="300px">
          <PopoverArrow />
          <PopoverHeader fontWeight="bold">More settings</PopoverHeader>
          <PopoverCloseButton />
          <PopoverBody>
            {isLoadingBranches ? (
              <HStack h="150px" justify="center">
                <Spinner />
              </HStack>
            ) : (
              <VStack as="div" spacing={4} align="stretch">
                <MultiSelectMenu
                  label="Branch"
                  options={branches}
                  selectedValues={settings.selectedBranches}
                  onSelectionChange={(selectedBranches) =>
                    setSettings((current) => ({ ...current, selectedBranches }))
                  }
                />
                <FormControl>
                  <FormLabel fontSize="sm">P&L opening balances</FormLabel>
                  <Select
                    size="sm"
                    value={settings.with_pnl_openings}
                    onChange={(event) =>
                      setSettings((current) => ({
                        ...current,
                        with_pnl_openings: event.target.value,
                      }))
                    }
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Zero balance accounts</FormLabel>
                  <Select
                    size="sm"
                    value={settings.include_zero_balances}
                    onChange={(event) =>
                      setSettings((current) => ({
                        ...current,
                        include_zero_balances: event.target.value,
                      }))
                    }
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </Select>
                </FormControl>
                <HStack justify="end">
                  <ReportActionButton
                    variant="ghost"
                    onClick={() =>
                      onApplySettings({
                        branches: [],
                        include_zero_balances: false,
                        with_pnl_openings: false,
                      })
                    }
                  >
                    Clear
                  </ReportActionButton>
                  <ReportActionButton
                    variant="primary"
                    onClick={() =>
                      onApplySettings({
                        branches: settings.selectedBranches,
                        include_zero_balances:
                          settings.include_zero_balances === "true",
                        with_pnl_openings:
                          settings.with_pnl_openings === "true",
                      })
                    }
                  >
                    Apply
                  </ReportActionButton>
                </HStack>
              </VStack>
            )}
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </HStack>
  );
};
